import React, { useState } from 'react';
import ResourceCanvas from '../components/ResourceCanvas';

export default function SysmeshDashboard() {
  const [activeTab, setActiveTab] = useState('principal');
  const [expandedAccounts, setExpandedAccounts] = useState({});
  const [expandedResources, setExpandedResources] = useState({});

  const toggleAccount = (accountId) => {
    setExpandedAccounts(prev => ({
      ...prev,
      [accountId]: !prev[accountId]
    }));
  };

  const toggleResource = (resourceId) => {
    setExpandedResources(prev => ({
      ...prev,
      [resourceId]: !prev[resourceId]
    }));
  };

  const styles = {
    container: {
      display: 'flex',
      height: '100vh',
      background: '#f8f8f8',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Red Hat Display", Roboto, "Helvetica Neue", Arial, sans-serif',
      overflow: 'hidden'
    },
    sidebar: {
      width: '280px',
      background: '#ffffff',
      borderRight: '1px solid #e5e5e5',
      display: 'flex',
      flexDirection: 'column',
      flexShrink: 0
    },
    sidebarHeader: {
      padding: '24px',
      borderBottom: '1px solid #e5e5e5'
    },
    logo: {
      fontSize: '24px',
      fontWeight: '700',
      color: '#ee0000',
      letterSpacing: '-0.5px',
      display: 'flex',
      alignItems: 'center',
      gap: '12px'
    },
    logoIcon: {
      width: '32px',
      height: '32px',
      background: 'linear-gradient(135deg, #ee0000 0%, #cc0000 100%)',
      borderRadius: '8px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: '#ffffff',
      fontSize: '16px',
      fontWeight: 'bold'
    },
    nav: {
      padding: '16px',
      flex: 1,
      overflowY: 'auto'
    },
    navItem: (active) => ({
      padding: '12px 16px',
      marginBottom: '8px',
      borderRadius: '8px',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      background: active ? '#fee' : 'transparent',
      color: active ? '#ee0000' : '#525252',
      fontWeight: active ? '600' : '500',
      fontSize: '15px',
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      border: active ? '1px solid #fdd' : '1px solid transparent'
    }),
    navIcon: {
      fontSize: '20px',
      width: '24px',
      textAlign: 'center'
    },
    sidebarFooter: {
      padding: '24px',
      borderTop: '1px solid #e5e5e5'
    },
    userInfo: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      cursor: 'pointer',
      padding: '12px',
      borderRadius: '8px',
      transition: 'all 0.2s ease'
    },
    userAvatar: {
      width: '40px',
      height: '40px',
      borderRadius: '50%',
      background: 'linear-gradient(135deg, #ee0000 0%, #cc0000 100%)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: '#ffffff',
      fontWeight: '600',
      fontSize: '16px'
    },
    userName: {
      flex: 1
    },
    userNameText: {
      fontSize: '14px',
      fontWeight: '600',
      color: '#151515',
      marginBottom: '2px'
    },
    userEmail: {
      fontSize: '12px',
      color: '#888'
    },
    mainContent: {
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden'
    },
    topBar: {
      padding: '24px 40px',
      background: '#ffffff',
      borderBottom: '1px solid #e5e5e5',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center'
    },
    topBarTitle: {
      fontSize: '28px',
      fontWeight: '700',
      color: '#151515',
      letterSpacing: '-0.5px'
    },
    topBarActions: {
      display: 'flex',
      gap: '12px'
    },
    button: (variant = 'secondary') => ({
      padding: '10px 24px',
      border: variant === 'primary' ? 'none' : '1px solid #e5e5e5',
      background: variant === 'primary' ? '#ee0000' : '#ffffff',
      color: variant === 'primary' ? '#ffffff' : '#525252',
      cursor: 'pointer',
      fontSize: '14px',
      fontWeight: '600',
      letterSpacing: '-0.2px',
      transition: 'all 0.2s ease',
      borderRadius: '6px',
      boxShadow: variant === 'primary' ? '0 2px 8px rgba(238, 0, 0, 0.2)' : 'none'
    }),
    contentArea: {
      flex: 1,
      padding: '40px',
      overflowY: 'auto'
    },
    statsGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(4, 1fr)',
      gap: '24px',
      marginBottom: '40px'
    },
    statCard: {
      padding: '24px',
      background: '#ffffff',
      borderRadius: '12px',
      border: '1px solid #e5e5e5',
      boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
    },
    statLabel: {
      fontSize: '14px',
      color: '#888',
      marginBottom: '8px',
      fontWeight: '500'
    },
    statValue: {
      fontSize: '32px',
      fontWeight: '700',
      color: '#151515',
      letterSpacing: '-1px',
      marginBottom: '8px'
    },
    statChange: (positive) => ({
      fontSize: '14px',
      color: positive ? '#10b981' : '#ef4444',
      fontWeight: '600'
    }),
    accountsList: {
      display: 'flex',
      flexDirection: 'column',
      gap: '16px'
    },
    accountCard: {
      background: '#ffffff',
      borderRadius: '12px',
      border: '1px solid #e5e5e5',
      overflow: 'hidden',
      boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
    },
    accountHeader: {
      padding: '20px 24px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      cursor: 'pointer',
      transition: 'all 0.2s ease'
    },
    accountInfo: {
      display: 'flex',
      alignItems: 'center',
      gap: '16px',
      flex: 1
    },
    accountIcon: {
      width: '48px',
      height: '48px',
      borderRadius: '8px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '24px',
      background: '#f8f8f8'
    },
    accountDetails: {
      flex: 1
    },
    accountName: {
      fontSize: '18px',
      fontWeight: '600',
      color: '#151515',
      marginBottom: '4px',
      letterSpacing: '-0.3px'
    },
    accountMeta: {
      fontSize: '14px',
      color: '#888'
    },
    accountStatus: (status) => ({
      padding: '6px 12px',
      borderRadius: '6px',
      fontSize: '12px',
      fontWeight: '600',
      background: status === 'active' ? '#d1fae5' : '#fee2e2',
      color: status === 'active' ? '#065f46' : '#991b1b',
      marginRight: '12px'
    }),
    expandIcon: (expanded) => ({
      fontSize: '20px',
      color: '#888',
      transition: 'transform 0.2s ease',
      transform: expanded ? 'rotate(90deg)' : 'rotate(0deg)'
    }),
    accountContent: (expanded) => ({
      maxHeight: expanded ? '1000px' : '0',
      overflow: 'hidden',
      transition: 'max-height 0.3s ease',
      borderTop: expanded ? '1px solid #e5e5e5' : 'none'
    }),
    resourcesList: {
      padding: '16px 24px'
    },
    resourceItem: {
      padding: '12px 16px',
      marginBottom: '8px',
      borderRadius: '8px',
      background: '#f8f8f8',
      cursor: 'pointer',
      transition: 'all 0.2s ease'
    },
    resourceHeader: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between'
    },
    resourceInfo: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      flex: 1
    },
    resourceName: {
      fontSize: '15px',
      fontWeight: '600',
      color: '#151515',
      marginBottom: '2px'
    },
    resourceType: {
      fontSize: '13px',
      color: '#888'
    },
    resourceDetails: (expanded) => ({
      marginTop: expanded ? '12px' : '0',
      paddingTop: expanded ? '12px' : '0',
      borderTop: expanded ? '1px solid #e5e5e5' : 'none',
      maxHeight: expanded ? '500px' : '0',
      overflow: 'hidden',
      transition: 'all 0.3s ease'
    }),
    instancesList: {
      display: 'flex',
      flexDirection: 'column',
      gap: '8px'
    },
    instanceItem: {
      padding: '10px 12px',
      background: '#ffffff',
      borderRadius: '6px',
      fontSize: '13px',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      border: '1px solid #e5e5e5'
    },
    instanceName: {
      fontWeight: '500',
      color: '#151515'
    },
    instanceStatus: {
      fontSize: '11px',
      fontWeight: '600',
      padding: '4px 8px',
      borderRadius: '4px',
      background: '#d1fae5',
      color: '#065f46'
    },
    emptyState: {
      textAlign: 'center',
      padding: '60px 20px',
      color: '#888'
    },
    emptyIcon: {
      fontSize: '48px',
      marginBottom: '16px'
    },
    emptyTitle: {
      fontSize: '20px',
      fontWeight: '600',
      color: '#151515',
      marginBottom: '8px'
    },
    emptyText: {
      fontSize: '15px',
      color: '#888',
      marginBottom: '24px'
    }
  };

  // Datos de ejemplo
  const accounts = [
    {
      id: 'aws-1',
      name: 'AWS Production',
      type: 'AWS',
      icon: '☁️',
      status: 'active',
      region: 'us-east-1',
      resources: [
        {
          id: 'ec2-1',
          name: 'EC2 Instances',
          type: 'Compute',
          count: 12,
          instances: [
            { id: 'i-001', name: 'web-server-01', status: 'running' },
            { id: 'i-002', name: 'web-server-02', status: 'running' },
            { id: 'i-003', name: 'api-server-01', status: 'running' },
            { id: 'i-004', name: 'worker-01', status: 'stopped' }
          ]
        },
        {
          id: 'rds-1',
          name: 'RDS Databases',
          type: 'Database',
          count: 3,
          instances: [
            { id: 'db-001', name: 'postgres-prod', status: 'running' },
            { id: 'db-002', name: 'mysql-analytics', status: 'running' },
            { id: 'db-003', name: 'redis-cache', status: 'running' }
          ]
        },
        {
          id: 's3-1',
          name: 'S3 Buckets',
          type: 'Storage',
          count: 8,
          instances: [
            { id: 's3-001', name: 'app-assets', status: 'active' },
            { id: 's3-002', name: 'user-uploads', status: 'active' },
            { id: 's3-003', name: 'backups', status: 'active' }
          ]
        }
      ]
    },
    {
      id: 'azure-1',
      name: 'Azure Development',
      type: 'Azure',
      icon: '🔷',
      status: 'active',
      region: 'East US',
      resources: [
        {
          id: 'vm-1',
          name: 'Virtual Machines',
          type: 'Compute',
          count: 5,
          instances: [
            { id: 'vm-001', name: 'dev-vm-01', status: 'running' },
            { id: 'vm-002', name: 'test-vm-01', status: 'stopped' }
          ]
        },
        {
          id: 'sql-1',
          name: 'SQL Databases',
          type: 'Database',
          count: 2,
          instances: [
            { id: 'sql-001', name: 'dev-db', status: 'running' }
          ]
        }
      ]
    },
    {
      id: 'k8s-1',
      name: 'Kubernetes Cluster',
      type: 'Kubernetes',
      icon: '☸️',
      status: 'active',
      region: 'GKE us-central1',
      resources: [
        {
          id: 'nodes-1',
          name: 'Nodes',
          type: 'Infrastructure',
          count: 6,
          instances: [
            { id: 'node-001', name: 'node-pool-1-abc', status: 'ready' },
            { id: 'node-002', name: 'node-pool-1-def', status: 'ready' },
            { id: 'node-003', name: 'node-pool-2-ghi', status: 'ready' }
          ]
        },
        {
          id: 'pods-1',
          name: 'Pods',
          type: 'Workload',
          count: 45,
          instances: [
            { id: 'pod-001', name: 'frontend-7d8f9', status: 'running' },
            { id: 'pod-002', name: 'backend-5k2l3', status: 'running' },
            { id: 'pod-003', name: 'worker-9m4n5', status: 'running' }
          ]
        }
      ]
    }
  ];

  const renderPrincipal = () => (
    <>
      <div style={styles.statsGrid}>
        <div style={styles.statCard}>
          <div style={styles.statLabel}>Total Resources</div>
          <div style={styles.statValue}>127</div>
          <div style={styles.statChange(true)}>↑ 12% vs last month</div>
        </div>
        <div style={styles.statCard}>
          <div style={styles.statLabel}>Active Accounts</div>
          <div style={styles.statValue}>8</div>
          <div style={styles.statChange(true)}>↑ 2 new this week</div>
        </div>
        <div style={styles.statCard}>
          <div style={styles.statLabel}>Uptime</div>
          <div style={styles.statValue}>99.9%</div>
          <div style={styles.statChange(true)}>↑ 0.2%</div>
        </div>
        <div style={styles.statCard}>
          <div style={styles.statLabel}>Monthly Cost</div>
          <div style={styles.statValue}>$4.2K</div>
          <div style={styles.statChange(false)}>↓ 8% optimized</div>
        </div>
      </div>

      <div style={{background: '#ffffff', borderRadius: '12px', padding: '24px', border: '1px solid #e5e5e5'}}>
        <h3 style={{fontSize: '20px', fontWeight: '700', marginBottom: '20px', color: '#151515'}}>
          Quick Overview
        </h3>
        <div style={{fontSize: '15px', color: '#525252', lineHeight: '1.7'}}>
          <p style={{marginBottom: '16px'}}>
            Bienvenido a tu dashboard de Sysmesh. Aquí puedes monitorear toda tu infraestructura desde un solo lugar.
          </p>
          <p>
            Tienes <strong style={{color: '#ee0000'}}>3 cuentas activas</strong> conectadas y <strong style={{color: '#ee0000'}}>127 recursos</strong> bajo gestión.
          </p>
        </div>
      </div>
    </>
  );

  const renderConexion = () => (
    <div style={styles.accountsList}>
      {accounts.map(account => (
        <div key={account.id} style={styles.accountCard}>
          <div
            style={styles.accountHeader}
            onClick={() => toggleAccount(account.id)}
          >
            <div style={styles.accountInfo}>
              <div style={styles.accountIcon}>{account.icon}</div>
              <div style={styles.accountDetails}>
                <div style={styles.accountName}>{account.name}</div>
                <div style={styles.accountMeta}>
                  {account.type} • {account.region} • {account.resources.reduce((sum, r) => sum + r.count, 0)} recursos
                </div>
              </div>
            </div>
            <div style={{display: 'flex', alignItems: 'center'}}>
              <span style={styles.accountStatus(account.status)}>
                {account.status === 'active' ? 'Activo' : 'Inactivo'}
              </span>
              <span style={styles.expandIcon(expandedAccounts[account.id])}>›</span>
            </div>
          </div>

          <div style={styles.accountContent(expandedAccounts[account.id])}>
            <div style={styles.resourcesList}>
              {account.resources.map(resource => (
                <div key={resource.id} style={styles.resourceItem}>
                  <div
                    style={styles.resourceHeader}
                    onClick={() => toggleResource(resource.id)}
                  >
                    <div style={styles.resourceInfo}>
                      <div>
                        <div style={styles.resourceName}>
                          {resource.name} ({resource.count})
                        </div>
                        <div style={styles.resourceType}>{resource.type}</div>
                      </div>
                    </div>
                    <span style={styles.expandIcon(expandedResources[resource.id])}>›</span>
                  </div>

                  <div style={styles.resourceDetails(expandedResources[resource.id])}>
                    <div style={styles.instancesList}>
                      {resource.instances.map(instance => (
                        <div key={instance.id} style={styles.instanceItem}>
                          <span style={styles.instanceName}>{instance.name}</span>
                          <span style={styles.instanceStatus}>{instance.status}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      ))}
    </div>
  );

  const renderGestion = () => (
    <div style={styles.emptyState}>
      {/* <div style={styles.emptyIcon}>🔧</div>
      <div style={styles.emptyTitle}>Gestión de Recursos</div>
      <div style={styles.emptyText}>
        Administra, escala y optimiza tus recursos desde aquí
      </div>
      <button style={styles.button('primary')}>
        Configurar Recursos
      </button> */}
      <ResourceCanvas />
    </div>
  );

  return (
    <div style={styles.container}>
      <style>{`
        button:hover {
          transform: translateY(-2px);
          opacity: 0.95;
        }
        .nav-item:hover {
          background: #f8f8f8 !important;
        }
        .user-info:hover {
          background: #f8f8f8;
        }
        .account-header:hover {
          background: #fafafa;
        }
        .resource-item:hover {
          background: #f0f0f0;
        }
        ::-webkit-scrollbar {
          width: 8px;
          height: 8px;
        }
        ::-webkit-scrollbar-track {
          background: #f1f1f1;
        }
        ::-webkit-scrollbar-thumb {
          background: #ccc;
          border-radius: 4px;
        }
        ::-webkit-scrollbar-thumb:hover {
          background: #999;
        }
      `}</style>

      <aside style={styles.sidebar}>
        <div style={styles.sidebarHeader}>
          <div style={styles.logo}>
            <div style={styles.logoIcon}>SM</div>
            <span>Sysmesh</span>
          </div>
        </div>

        <nav style={styles.nav}>
          <div
            className="nav-item"
            style={styles.navItem(activeTab === 'principal')}
            onClick={() => setActiveTab('principal')}
          >
            <span style={styles.navIcon}>📊</span>
            <span>Principal</span>
          </div>
          <div
            className="nav-item"
            style={styles.navItem(activeTab === 'conexion')}
            onClick={() => setActiveTab('conexion')}
          >
            <span style={styles.navIcon}>🔌</span>
            <span>Conexión</span>
          </div>
          <div
            className="nav-item"
            style={styles.navItem(activeTab === 'gestion')}
            onClick={() => setActiveTab('gestion')}
          >
            <span style={styles.navIcon}>⚙️</span>
            <span>Gestión de Recursos</span>
          </div>
        </nav>

        <div style={styles.sidebarFooter}>
          <div className="user-info" style={styles.userInfo}>
            <div style={styles.userAvatar}>JD</div>
            <div style={styles.userName}>
              <div style={styles.userNameText}>Juan Díaz</div>
              <div style={styles.userEmail}>juan@empresa.com</div>
            </div>
          </div>
        </div>
      </aside>

      <main style={styles.mainContent}>
        <div style={styles.topBar}>
          <h1 style={styles.topBarTitle}>
            {activeTab === 'principal' && 'Dashboard Principal'}
            {activeTab === 'conexion' && 'Cuentas Conectadas'}
            {activeTab === 'gestion' && 'Gestión de Recursos'}
          </h1>
          <div style={styles.topBarActions}>
            <button style={styles.button()}>Exportar</button>
            <button style={styles.button('primary')}>+ Conectar Cuenta</button>
          </div>
        </div>

        <div style={styles.contentArea}>
          {activeTab === 'principal' && renderPrincipal()}
          {activeTab === 'conexion' && renderConexion()}
          {activeTab === 'gestion' && renderGestion()}
        </div>
      </main>
    </div>
  );
}